import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { Profile, User } from '../../shared/model/models'
import { WALKTHROUGH_STATUS } from '../../shared/constants'
import { updateProfile } from '../actions/user/thunk'

type UserState = {
  token: string | null
  refreshToken: string | null
  company: string | null
  account: User | null
  isAuthenticated: boolean
  currentTheme: 'light' | 'dark'
  currentLanguage: 'de' | 'en'
  walkthroughStatus: WALKTHROUGH_STATUS
  walkthroughIndex: number
  isWalkthroughVideo: boolean
  askAccess: string | null
  htmlFontSize: number
  lastKnownLocation: string | null
}

export const initialState: UserState = {
  token: null,
  refreshToken: null,
  company: null,
  account: null,
  isAuthenticated: false,
  currentTheme: 'light',
  currentLanguage: 'de',
  walkthroughStatus: WALKTHROUGH_STATUS.NotStarted,
  walkthroughIndex: 0,
  isWalkthroughVideo: false,
  askAccess: null,
  htmlFontSize: 16,
  lastKnownLocation: null,
}

const userSlice = createSlice({
  name: 'currentUser',
  initialState,
  reducers: {
    login: (
      state,
      action: PayloadAction<{
        token: string
        refreshToken: string
        company: string
        account: User
        isAuthenticated: boolean
      }>,
    ) => {
      const { token, refreshToken, company, account, isAuthenticated } = action.payload
      Object.assign(state, {
        token,
        refreshToken,
        company,
        account: { ...account, profile: account.profile },
        isAuthenticated,
      })
    },
    logout: (state) => {
      const { currentTheme, currentLanguage, htmlFontSize, ...rest } = initialState
      Object.assign(state, rest)
    },
    logoutWithLocationUser: (state, action: PayloadAction<string | null>) => {
      const { currentTheme, currentLanguage, htmlFontSize, ...rest } = initialState
      Object.assign(state, { ...rest, lastKnownLocation: action.payload })
    },
    setAuthTokens: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
      const { token, refreshToken } = action.payload
      Object.assign(state, { token, refreshToken })
    },
    setCurrentTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.currentTheme = action.payload
    },
    setCurrentLanguage: (state, action: PayloadAction<'de' | 'en'>) => {
      state.currentLanguage = action.payload
    },
    setWalkthroughStatus: (state, action: PayloadAction<WALKTHROUGH_STATUS>) => {
      state.walkthroughStatus = action.payload
    },
    setWalkthroughIndex: (state, action: PayloadAction<number>) => {
      state.walkthroughIndex = action.payload
    },
    startWalkthrough: (state) => {
      state.walkthroughIndex = 0
      state.walkthroughStatus = WALKTHROUGH_STATUS.InProgress
    },
    setWalkthroughVideo: (state, action: PayloadAction<boolean>) => {
      state.isWalkthroughVideo = action.payload
    },
    updateProfileState: (state, action: PayloadAction<Profile>) => {
      if (state.account) state.account.profile = action.payload
    },
    setAskAccess: (state, action: PayloadAction<string | null>) => {
      state.askAccess = action.payload
    },
    setHtmlFontSize: (state, action: PayloadAction<number>) => {
      state.htmlFontSize = action.payload
    },
    clearLastKnownLocation: (state) => {
      state.lastKnownLocation = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase(updateProfile.fulfilled, (state, action: PayloadAction<Profile>) => {
      if (state.account) {
        if (state.account.profile) Object.assign(state.account.profile, action.payload)
        else state.account.profile = action.payload
      }
    })
  },
})

export const userActions = userSlice.actions
export default userSlice.reducer
